import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const FALLBACK_CASES = [
  {
    id: 'case-1',
    label: 'Hollywood Smile · 16 Veneers',
    before: 'https://images.unsplash.com/photo-1581585504094-a1d3c8e2f1f0?w=1200&q=80',
    after: 'https://images.unsplash.com/photo-1607613009820-a29f7bb81c04?w=1200&q=80',
  },
  {
    id: 'case-2',
    label: 'Full Mouth Implants',
    before: 'https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=1200&q=80',
    after: 'https://images.unsplash.com/photo-1612538498488-3091acaaca3a?w=1200&q=80',
  },
  {
    id: 'case-3',
    label: 'Zirconium Crowns · Full Arch',
    before: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=1200&q=80',
    after: 'https://images.unsplash.com/photo-1611073615452-4889bcdb1620?w=1200&q=80',
  },
];

const Comparator = ({ before, after, beforeLabel, afterLabel, label }) => {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, ratio)));
  }, []);

  useEffect(() => {
    const handleMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClientX(x);
    };
    const handleEnd = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: true });
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [updateFromClientX]);

  const startDrag = (e) => {
    draggingRef.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(x);
  };

  return (
    <div
      ref={wrapRef}
      className="ba-compare"
      style={{ '--pos': `${pos}%` }}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      role="img"
      aria-label={label}
    >
      <img src={after} alt={`${label} after`} className="ba-img ba-img-after" />
      <img src={before} alt={`${label} before`} className="ba-img ba-img-before" />
      <span className="ba-label ba-label-before">{beforeLabel}</span>
      <span className="ba-label ba-label-after">{afterLabel}</span>
      <button
        type="button"
        className="ba-handle"
        style={{ left: `${pos}%` }}
        aria-label="Drag to compare"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      >
        <span className="ba-handle-dot" />
      </button>
      <span className="ba-caption">{label}</span>
    </div>
  );
};

const BeforeAfterSection = ({ cases = FALLBACK_CASES }) => {
  const { t } = useLanguage();
  const beforeLabel = t('beforeAfter.before', 'Before');
  const afterLabel = t('beforeAfter.after', 'After');

  return (
    <section id="before-after" className="section-padding section-tone-c">
      <div className="container-premium">
        <div className="section-head">
          <span className="section-eyebrow">{t('beforeAfter.eyebrow', 'Real Results')}</span>
          <h2 className="section-title">{t('beforeAfter.title', 'Before & After')}</h2>
          <p className="section-sub">
            {t('beforeAfter.subtitle', 'Real patient transformations — drag the slider to see the change.')}
          </p>
        </div>

        <div className="ba-grid">
          {cases.map((c) => (
            <Comparator
              key={c.id}
              before={c.before}
              after={c.after}
              label={c.label}
              beforeLabel={beforeLabel}
              afterLabel={afterLabel}
            />
          ))}
        </div>

        <div className="section-foot">
          <Link to="/gallery" className="btn-crystal-outline">
            {t('beforeAfter.viewAll', 'See more transformations')} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
